import { useState, useMemo, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Phone,
  PhoneIncoming,
  Search,
  RefreshCw,
  Loader2,
  ShieldCheck,
  Sliders,
  Bot,
  Check,
  AlertCircle,
  Lock,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { CopyButton } from "../../components/ui/copy-button";
import { Input } from "../../components/ui/input";
import { useToast } from "../../components/ui/toast";
import { Modal } from "../../components/ui/modal";
import { ToggleSwitch } from "../../components/ui/toggle-switch";
import { AssignmentFields, useAssignment } from "./assignment-fields";
import { WhatsAppNumbersTab } from "./whatsapp-numbers-tab";

const apiUrl = process.env.REACT_APP_BACKEND_URL || "";

/** The agent answering a number. `status` is LiveKit's side, set by core's reconciler. */
export interface InboundRoute {
  agent_id: string;
  version_id: string;
  voice: string;
  status: "pending" | "live" | "conflict" | "error" | string;
  status_detail?: string | null;
}

export interface InboundNumber {
  did_number_id: string;
  number: string;
  provider?: string | null;
  active: boolean;
  /** Only when this account holds the number. */
  route: InboundRoute | null;
  claimed_by_other: boolean;
}

interface ConfigureModalProps {
  item: InboundNumber;
  onClose: () => void;
  onSaved: (updated: InboundNumber) => void;
}

const auth = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });

export function ConfigureReceptionistModal({ item, onClose, onSaved }: ConfigureModalProps) {
  const { token } = useAuth();
  const toast = useToast();
  const assignment = useAssignment(token, item.route);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !assignment.complete) return;
    try {
      setSaving(true);
      setError(null);
      const res = await axios.put(
        `${apiUrl}/api/v1/inbound/numbers/${encodeURIComponent(item.did_number_id)}`,
        assignment.value,
        auth(token)
      );
      toast.success("Receptionist saved", `${item.number} will answer with this agent within a minute.`);
      onSaved(res.data);
      onClose();
    } catch (err: any) {
      const msg = err.response?.data?.detail || "Failed to save the receptionist.";
      setError(msg);
      toast.danger("Configuration failed", msg);
    } finally {
      setSaving(false);
    }
  };

  const shownError = error || assignment.error;
  return (
    <Modal
      title="Configure Inbound Receptionist"
      subtitle={`Configure the AI receptionist agent and voice for ${item.number}`}
      onClose={onClose}
      width="max-w-xl"
    >
      {assignment.loading ? (
        <div className="flex flex-col items-center justify-center p-8 gap-3 text-ink-3">
          <Loader2 className="w-6 h-6 animate-spin text-brand" />
          <p className="text-sm">Loading available agents and voices...</p>
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-5">
          {shownError && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{shownError}</span>
            </div>
          )}

          <div className="p-3.5 rounded-lg border border-line bg-paper-2/60 flex items-center justify-between">
            <div>
              <span className="text-xs font-mono text-ink-3 uppercase tracking-wider block">DID Target</span>
              <span className="text-base font-semibold font-mono text-ink">{item.number}</span>
            </div>
            <div className="text-right">
              <span className="text-xs font-mono text-ink-3 uppercase tracking-wider block">Provider</span>
              <span className="text-sm font-medium text-ink capitalize">{item.provider || "Standard"}</span>
            </div>
          </div>

          <AssignmentFields idPrefix="inbound" assignment={assignment} />

          <div className="pt-3 border-t border-line flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-line bg-paper text-ink hover:bg-paper-2 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !assignment.complete}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-brand text-white hover:bg-brand/90 transition shadow-sm disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save Configuration
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function RouteStatus({ route }: { route: InboundRoute }) {
  if (route.status === "live") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
        <span className="w-2 h-2 rounded-full bg-emerald-500" />
        Live
      </span>
    );
  }
  if (route.status === "conflict" || route.status === "error") {
    return (
      <span
        title={route.status_detail || undefined}
        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20"
      >
        <AlertCircle className="w-3 h-3" />
        Needs attention
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-400">
      <Loader2 className="w-3 h-3 animate-spin" />
      Setting up
    </span>
  );
}

export function PhoneNumbersPage() {
  const { token } = useAuth();
  const [numbers, setNumbers] = useState<InboundNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [releasingId, setReleasingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [configuring, setConfiguring] = useState<InboundNumber | null>(null);
  const [channel, setChannel] = useState<"carrier" | "whatsapp">("carrier");
  const toast = useToast();

  const fetchNumbers = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${apiUrl}/api/v1/inbound/numbers`, auth(token));
      setNumbers(Array.isArray(res.data?.numbers) ? res.data.numbers : []);
    } catch (err: any) {
      const msg = err.response?.data?.detail || "Failed to load phone numbers.";
      setError(msg);
      toast.danger("Failed to load phone numbers", msg);
    } finally {
      setLoading(false);
    }
  }, [token, toast]);

  useEffect(() => {
    fetchNumbers();
  }, [fetchNumbers]);

  const replace = (updated: InboundNumber) =>
    setNumbers((prev) => prev.map((n) => (n.did_number_id === updated.did_number_id ? updated : n)));

  const handleToggleInbound = async (item: InboundNumber) => {
    if (item.claimed_by_other) {
      toast.warning("Number unavailable", "Another account already answers this number.");
      return;
    }
    if (!item.route) {
      setConfiguring(item);
      return;
    }
    try {
      setReleasingId(item.did_number_id);
      const res = await axios.delete(
        `${apiUrl}/api/v1/inbound/numbers/${encodeURIComponent(item.did_number_id)}`,
        auth(token!)
      );
      toast.success("Inbound disabled", `${item.number} no longer answers with an agent.`);
      replace(res.data);
    } catch (err: any) {
      toast.danger("Action failed", err.response?.data?.detail || "Failed to disable inbound.");
    } finally {
      setReleasingId(null);
    }
  };

  const filteredNumbers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return numbers.filter((entry) => {
      if (!q) return true;
      return (
        String(entry.number || "").toLowerCase().includes(q) ||
        String(entry.provider || "").toLowerCase().includes(q) ||
        String(entry.route?.agent_id || "").toLowerCase().includes(q) ||
        String(entry.route?.voice || "").toLowerCase().includes(q)
      );
    });
  }, [numbers, searchQuery]);

  const stats = useMemo(
    () => ({
      total: numbers.length,
      inboundActive: numbers.filter((n) => n.route).length,
      available: numbers.filter((n) => !n.route && !n.claimed_by_other).length,
    }),
    [numbers]
  );

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Phone Numbers</h1>
          <p className="mt-1 text-sm text-ink-2">
            Manage phone numbers assigned to your organization and configure inbound AI receptionists.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchNumbers}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-lg border border-line bg-paper text-ink hover:bg-paper-2 transition disabled:opacity-50"
            title="Refresh inventory"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-xl border border-line bg-paper/50 backdrop-blur-sm shadow-sm space-y-1">
          <div className="flex items-center justify-between text-ink-3">
            <span className="text-xs font-mono uppercase tracking-wider">Total Numbers</span>
            <Phone className="w-4 h-4 text-brand" />
          </div>
          <div className="text-2xl font-bold font-mono text-ink">{stats.total}</div>
        </div>
        <div className="p-4 rounded-xl border border-line bg-paper/50 backdrop-blur-sm shadow-sm space-y-1">
          <div className="flex items-center justify-between text-ink-3">
            <span className="text-xs font-mono uppercase tracking-wider">Inbound Active</span>
            <PhoneIncoming className="w-4 h-4 text-emerald-500" />
          </div>
          <div className="text-2xl font-bold font-mono text-emerald-600 dark:text-emerald-400">{stats.inboundActive}</div>
        </div>
        <div className="p-4 rounded-xl border border-line bg-paper/50 backdrop-blur-sm shadow-sm space-y-1">
          <div className="flex items-center justify-between text-ink-3">
            <span className="text-xs font-mono uppercase tracking-wider">Available for Inbound</span>
            <ShieldCheck className="w-4 h-4 text-brand" />
          </div>
          <div className="text-2xl font-bold font-mono text-ink">{stats.available}</div>
        </div>
      </div>

      <div className="flex items-center gap-1 p-1 rounded-lg border border-line bg-paper-2/60 w-fit">
        {(["carrier", "whatsapp"] as const).map((c) => (
          <button
            key={c}
            onClick={() => setChannel(c)}
            className={`px-3.5 py-1.5 text-xs font-medium rounded-md transition ${
              channel === c ? "bg-paper text-ink shadow-sm border border-line" : "text-ink-3 hover:text-ink"
            }`}
          >
            {c === "carrier" ? "Carrier DIDs" : "WhatsApp"}
          </button>
        ))}
      </div>

      <div className="p-4 rounded-xl border border-line bg-paper shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-ink-3" />
          <Input
            placeholder="Search number, agent, provider..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
      </div>

      {channel === "whatsapp" ? (
        <WhatsAppNumbersTab searchQuery={searchQuery} />
      ) : (
      <div className="rounded-xl border border-line bg-paper overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex flex-col items-center justify-center min-h-[320px] gap-2 text-ink-3">
            <Loader2 className="w-6 h-6 animate-spin text-brand" />
            <p className="text-sm">Loading phone numbers...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center min-h-[320px] gap-3 text-center p-6">
            <p className="text-sm text-danger">{error}</p>
            <button
              onClick={fetchNumbers}
              className="px-3.5 py-1.5 text-xs font-medium rounded-lg border border-danger text-danger hover:bg-danger/10 transition"
            >
              Try Again
            </button>
          </div>
        ) : filteredNumbers.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[320px] gap-3 text-center p-6 text-ink-3">
            <div className="p-3 rounded-full bg-paper-2 text-ink-2">
              <Phone className="w-6 h-6" />
            </div>
            <div>
              <p className="text-base font-medium text-ink">No phone numbers found</p>
              <p className="text-xs text-ink-3 mt-1">
                {searchQuery
                  ? "Try clearing your search query."
                  : "No phone numbers have been allocated to your organization yet. Contact your administrator to assign numbers."}
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-paper-2 border-b border-line text-ink-3 text-xs uppercase tracking-wider font-mono">
                <tr>
                  <th className="px-4 py-3">Phone Number</th>
                  <th className="px-4 py-3">Inbound Receptionist</th>
                  <th className="px-4 py-3">Assigned AI Receptionist</th>
                  <th className="px-4 py-3">Prompt Version</th>
                  <th className="px-4 py-3">Voice</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {filteredNumbers.map((num) => {
                  const route = num.route;
                  const isReleasing = releasingId === num.did_number_id;
                  return (
                    <tr key={num.did_number_id} className="hover:bg-paper-2/60 transition group">
                      <td className="px-4 py-3 font-mono font-medium text-ink whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <span>{num.number}</span>
                          <CopyButton value={num.number} className="opacity-0 group-hover:opacity-100 transition" />
                        </div>
                        {num.provider && (
                          <div className="text-[11px] font-sans font-normal text-ink-3">Provider: {num.provider}</div>
                        )}
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2.5">
                          {isReleasing ? (
                            <Loader2 className="w-5 h-5 animate-spin text-brand" />
                          ) : (
                            <ToggleSwitch
                              checked={Boolean(route)}
                              disabled={num.claimed_by_other}
                              onChange={() => handleToggleInbound(num)}
                              ariaLabel={`Toggle inbound receptionist for ${num.number}`}
                            />
                          )}
                          {num.claimed_by_other ? (
                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20">
                              <Lock className="w-3 h-3" />
                              Claimed by another account
                            </span>
                          ) : route ? (
                            <RouteStatus route={route} />
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-3">
                              <span className="w-2 h-2 rounded-full bg-slate-400" />
                              Disabled
                            </span>
                          )}
                        </div>
                        {route?.status_detail && (route.status === "conflict" || route.status === "error") && (
                          <div className="mt-1 text-[11px] text-red-600 dark:text-red-400 whitespace-normal max-w-xs">
                            {route.status_detail}
                          </div>
                        )}
                      </td>

                      <td className="px-4 py-3 text-ink-2 whitespace-nowrap">
                        {route ? (
                          <span className="inline-flex items-center gap-1.5 font-medium text-ink font-mono text-xs px-2.5 py-1 rounded-md bg-paper-2 border border-line">
                            {route.agent_id}
                          </span>
                        ) : (
                          <span className="text-ink-3 italic text-xs">{num.claimed_by_other ? "Unavailable" : "Disabled"}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-ink-2 whitespace-nowrap font-mono text-xs">
                        {route ? route.version_id : <span className="text-ink-3">—</span>}
                      </td>
                      <td className="px-4 py-3 text-ink-2 whitespace-nowrap text-xs">
                        {route ? route.voice : <span className="text-ink-3">—</span>}
                      </td>

                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {num.claimed_by_other ? (
                          <span className="inline-flex items-center gap-1 text-xs text-ink-3 italic px-2 py-1">
                            <Lock className="w-3.5 h-3.5" /> Claimed
                          </span>
                        ) : (
                          <button
                            onClick={() => setConfiguring(num)}
                            aria-label={`Configure ${num.number}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-lg border border-line bg-paper text-ink hover:border-brand/60 hover:text-brand hover:bg-brand/5 shadow-sm transition"
                          >
                            {route ? <Sliders className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5 text-brand" />}
                            {route ? "Configure Receptionist" : "Enable Inbound"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      )}

      {configuring && (
        <ConfigureReceptionistModal item={configuring} onClose={() => setConfiguring(null)} onSaved={replace} />
      )}
    </div>
  );
}
